/**
 * @module lib/helpers/format-currency
 * Formats a number as a localized currency string.
 *
 * Defaults:
 * - locale: 'en-US'
 * - currency: 'USD'
 * - currencyDisplay: 'narrowSymbol'
 * - hides `.00` decimals if not needed
 *
 * @example
 * import { formatCurrency } from 'lib/helpers';
 *
 * formatCurrency(1234.5);
 * // '$1,234.50'
 *
 * formatCurrency(1234.5, { currency: 'EUR', locale: 'de-DE' });
 * // '1.234,50 €'
 *
 * formatCurrency(50000, { currency: 'NGN' });
 * // '₦50,000'
 *
 * formatCurrency(50000.75, { currency: 'NGN' });
 * // '₦50,000.75'
 */
export function formatCurrency(
  value: number,
  {
    locale = 'en-US',
    currency = 'NGN',
    currencyDisplay = 'narrowSymbol',
  }: {
    locale?: string;
    currency?: string;
    currencyDisplay?: 'symbol' | 'narrowSymbol' | 'code' | 'name';
  } = {}
): string {
  const hasDecimals = value % 1 !== 0;

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay,
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  }).format(value);
}
